// ============================================
// OFFICEFORGED — Keyboard Shortcuts
// Standard Office/Google shortcuts people expect.
// Uses hotkeys-js (loaded via CDN in index.html).
// ============================================

export function setupKeyboardShortcuts({ sidebar, editor, pdfViewer }) {
  const hotkeys = window.hotkeys;
  if (!hotkeys) { console.warn('hotkeys-js not loaded — keyboard shortcuts disabled'); return; }

  // Allow shortcuts to fire inside contenteditable, inputs, textareas
  hotkeys.filter = function () { return true; };

  const getModule = () => sidebar.activeModule;

  // ═══════════════════════════════════════════
  // MODULE SWITCHING — Ctrl+1 through Ctrl+6
  // Same pattern as browser tabs
  // ═══════════════════════════════════════════
  const modules = ['docs', 'grid', 'pdf', 'files', 'chat', 'video'];
  modules.forEach((mod, i) => {
    hotkeys(`ctrl+${i + 1}`, (e) => {
      e.preventDefault();
      sidebar.switchModule(mod);
      syncDockPill(mod);
    });
  });

  // ═══════════════════════════════════════════
  // FILE OPERATIONS
  // ═══════════════════════════════════════════

  // Ctrl+N — New document / spreadsheet
  hotkeys('ctrl+n', (e) => {
    e.preventDefault();
    const mod = getModule();
    if (mod === 'docs') {
      if (!confirm('Create a new document? Unsaved changes will be lost.')) return;
      editor.newDocument();
    } else if (mod === 'grid') {
      if (!confirm('Create a new spreadsheet? Unsaved changes will be lost.')) return;
      window.location.reload();
    }
  });

  // Ctrl+O — Open file
  hotkeys('ctrl+o', (e) => {
    e.preventDefault();
    const mod = getModule();
    if (mod === 'pdf') {
      document.getElementById('pdf-open')?.click();
    } else if (mod === 'grid') {
      document.getElementById('grid-open-any')?.click();
    }
    // Docs: future file open dialog
  });

  // Ctrl+S — Save
  hotkeys('ctrl+s', (e) => {
    e.preventDefault();
    const mod = getModule();
    if (mod === 'docs' && editor) {
      editor._saveToStorage();
      flashSaveIndicator();
    } else if (mod === 'grid') {
      document.getElementById('grid-save-local')?.click();
    } else if (mod === 'pdf') {
      document.getElementById('pdf-download')?.click();
    }
  });

  // Ctrl+Shift+S — Save As / Export
  hotkeys('ctrl+shift+s', (e) => {
    e.preventDefault();
    const mod = getModule();
    if (mod === 'docs') {
      document.getElementById('btn-export')?.click();
    } else if (mod === 'grid') {
      document.getElementById('grid-export-xlsx')?.click();
    } else if (mod === 'pdf') {
      document.getElementById('pdf-download')?.click();
    }
  });

  // Ctrl+P — Print
  hotkeys('ctrl+p', (e) => {
    e.preventDefault();
    const mod = getModule();
    if (mod === 'docs') {
      document.getElementById('btn-print')?.click();
    } else if (mod === 'grid') {
      document.getElementById('grid-print')?.click();
    } else if (mod === 'pdf') {
      document.getElementById('pdf-print')?.click();
    }
  });

  // ═══════════════════════════════════════════
  // FIND & REPLACE
  // ═══════════════════════════════════════════

  // Ctrl+F — Find
  hotkeys('ctrl+f', (e) => {
    const mod = getModule();
    if (mod === 'docs') {
      e.preventDefault();
      document.getElementById('btn-find')?.click();
    } else if (mod === 'pdf') {
      e.preventDefault();
      document.getElementById('pdf-search')?.click();
    } else if (mod === 'grid') {
      e.preventDefault();
      document.getElementById('grid-search')?.click();
    }
    // else: let browser default handle it
  });

  // Ctrl+H — Find & Replace
  hotkeys('ctrl+h', (e) => {
    const mod = getModule();
    if (mod === 'docs') {
      e.preventDefault();
      document.getElementById('btn-find')?.click();
    } else if (mod === 'grid') {
      e.preventDefault();
      document.getElementById('grid-regex')?.click();
    }
  });

  // ═══════════════════════════════════════════
  // ZOOM — Ctrl+= / Ctrl+- / Ctrl+0
  // ═══════════════════════════════════════════

  hotkeys('ctrl+=,ctrl+shift+=', (e) => {
    e.preventDefault();
    adjustZoom(getModule(), 1);
  });

  hotkeys('ctrl+-', (e) => {
    e.preventDefault();
    adjustZoom(getModule(), -1);
  });

  hotkeys('ctrl+0', (e) => {
    e.preventDefault();
    adjustZoom(getModule(), 0);
  });

  // ═══════════════════════════════════════════
  // DOCUMENT FORMATTING — Docs module only
  // Matches Microsoft Word / Google Docs
  // ═══════════════════════════════════════════

  // Ctrl+E — Center align
  hotkeys('ctrl+e', (e) => {
    if (getModule() !== 'docs') return;
    e.preventDefault();
    document.getElementById('btn-align-center')?.click();
  });

  // Ctrl+L — Left align
  hotkeys('ctrl+l', (e) => {
    if (getModule() !== 'docs') return;
    e.preventDefault();
    document.getElementById('btn-align-left')?.click();
  });

  // Ctrl+R — Right align
  hotkeys('ctrl+r', (e) => {
    if (getModule() !== 'docs') return;
    e.preventDefault();
    document.getElementById('btn-align-right')?.click();
  });

  // Ctrl+J — Justify
  hotkeys('ctrl+j', (e) => {
    if (getModule() !== 'docs') return;
    e.preventDefault();
    document.getElementById('btn-align-justify')?.click();
  });

  // Ctrl+K — Insert hyperlink
  hotkeys('ctrl+k', (e) => {
    if (getModule() !== 'docs') return;
    e.preventDefault();
    document.getElementById('btn-link')?.click();
  });

  // Ctrl+Shift+8 — Bullet list (Google Docs standard)
  hotkeys('ctrl+shift+8', (e) => {
    if (getModule() !== 'docs') return;
    e.preventDefault();
    document.getElementById('btn-bullet-list')?.click();
  });

  // Ctrl+Shift+7 — Numbered list (Google Docs standard)
  hotkeys('ctrl+shift+7', (e) => {
    if (getModule() !== 'docs') return;
    e.preventDefault();
    document.getElementById('btn-ordered-list')?.click();
  });

  // Ctrl+] — Indent
  hotkeys('ctrl+]', (e) => {
    if (getModule() !== 'docs') return;
    e.preventDefault();
    document.getElementById('btn-indent')?.click();
  });

  // Ctrl+[ — Outdent
  hotkeys('ctrl+[', (e) => {
    if (getModule() !== 'docs') return;
    e.preventDefault();
    document.getElementById('btn-outdent')?.click();
  });

  // Ctrl+Shift+X — Strikethrough (supplement — also in ProseMirror)
  // ProseMirror handles it when editor is focused; this catches toolbar focus
  hotkeys('ctrl+shift+x', (e) => {
    if (getModule() !== 'docs') return;
    e.preventDefault();
    document.getElementById('btn-strike')?.click();
  });

  // Ctrl+\ — Clear formatting (Google Docs)
  hotkeys('ctrl+\\', (e) => {
    if (getModule() !== 'docs') return;
    e.preventDefault();
    document.getElementById('btn-clear-format')?.click();
  });

  // ═══════════════════════════════════════════
  // CARD INSPECTOR
  // ═══════════════════════════════════════════

  // Ctrl+Shift+I — Toggle card inspector panel
  hotkeys('ctrl+shift+i', (e) => {
    if (getModule() !== 'docs') return;
    e.preventDefault();
    document.getElementById('btn-toggle-cards')?.click();
  });

  // ═══════════════════════════════════════════
  // PDF NAVIGATION
  // ═══════════════════════════════════════════

  hotkeys('pageup', (e) => {
    if (getModule() !== 'pdf') return;
    e.preventDefault();
    document.getElementById('pdf-prev')?.click();
  });

  hotkeys('pagedown', (e) => {
    if (getModule() !== 'pdf') return;
    e.preventDefault();
    document.getElementById('pdf-next')?.click();
  });

  // ═══════════════════════════════════════════
  // ESCAPE — Close menus, dropdowns, modals
  // ═══════════════════════════════════════════

  hotkeys('escape', () => {
    // Close grid dropdown menus
    document.querySelectorAll('.grid-menu-dropdown').forEach(d => {
      d.style.display = 'none';
    });
    // Close donut selector if open
    const donutMenu = document.getElementById('donut-selector');
    if (donutMenu) donutMenu.style.display = 'none';
  });

  // ═══════════════════════════════════════════
  // FULLSCREEN — F11
  // ═══════════════════════════════════════════

  hotkeys('f11', (e) => {
    e.preventDefault();
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  });

  console.log('Keyboard shortcuts ready — Ctrl+/ for shortcut reference');

  // ═══════════════════════════════════════════
  // Ctrl+/ — Show shortcut reference
  // ═══════════════════════════════════════════

  hotkeys('ctrl+/', (e) => {
    e.preventDefault();
    showShortcutReference(getModule());
  });
}

// ─── Helpers ────────────────────────────────

function syncDockPill(module) {
  // Update dock-slot active states
  document.querySelectorAll('.dock-slot').forEach(btn => {
    btn.classList.toggle('dock-slot-active', btn.dataset.module === module);
  });

  // Update the coin face
  const coinIcon = document.getElementById('dock-coin-icon');
  const coinLabel = document.getElementById('dock-coin-label');
  const icons = { docs: '\u{1F4C4}', grid: '\u25A6', pdf: '\u{1F4D3}', files: '\u{1F4C1}', chat: '\u{1F4AC}', video: '\u{1F3A5}' };
  const labels = { docs: 'DOCS', grid: 'GRID', pdf: 'PDF', files: 'FILES', chat: 'CHAT', video: 'VIDEO' };
  if (coinIcon) coinIcon.textContent = icons[module] || '\u{1F4C4}';
  if (coinLabel) coinLabel.textContent = labels[module] || module.toUpperCase();
}

function flashSaveIndicator() {
  const el = document.getElementById('save-indicator');
  if (!el) return;
  el.textContent = '\u25CF Saved';
  el.style.color = '#4ade80';
  setTimeout(() => { el.style.color = ''; }, 1500);
}

function adjustZoom(module, direction) {
  if (module === 'docs') {
    const sel = document.getElementById('sel-zoom');
    if (!sel) return;
    const opts = Array.from(sel.options).map(o => parseInt(o.value));
    const cur = parseInt(sel.value);
    if (direction === 0) {
      sel.value = '100';
    } else {
      const idx = opts.indexOf(cur);
      const next = idx + direction;
      if (next >= 0 && next < opts.length) {
        sel.value = String(opts[next]);
      }
    }
    sel.dispatchEvent(new Event('change'));
  } else if (module === 'pdf') {
    if (direction === 0) {
      const sel = document.getElementById('pdf-zoom-select');
      if (sel) { sel.value = '1'; sel.dispatchEvent(new Event('change')); }
    } else if (direction === 1) {
      document.getElementById('pdf-zoom-in')?.click();
    } else {
      document.getElementById('pdf-zoom-out')?.click();
    }
  }
  // Grid: Univer handles its own zoom
}

function showShortcutReference(activeModule) {
  // Remove existing modal if open
  const existing = document.getElementById('shortcut-ref-modal');
  if (existing) { existing.remove(); return; }

  const globalShortcuts = [
    ['Ctrl + 1–6', 'Switch modules (Docs, Grid, PDF, Files, Chat, Video)'],
    ['Ctrl + N', 'New document / spreadsheet'],
    ['Ctrl + O', 'Open file'],
    ['Ctrl + S', 'Save'],
    ['Ctrl + Shift + S', 'Save As / Export'],
    ['Ctrl + P', 'Print'],
    ['Ctrl + F', 'Find'],
    ['Ctrl + H', 'Find & Replace'],
    ['Ctrl + =', 'Zoom in'],
    ['Ctrl + −', 'Zoom out'],
    ['Ctrl + 0', 'Reset zoom'],
    ['F11', 'Fullscreen'],
    ['Escape', 'Close menus'],
    ['Ctrl + /', 'This reference'],
  ];

  const docsShortcuts = [
    ['Ctrl + B', 'Bold'],
    ['Ctrl + I', 'Italic'],
    ['Ctrl + U', 'Underline'],
    ['Ctrl + Shift + X', 'Strikethrough'],
    ['Ctrl + Z', 'Undo'],
    ['Ctrl + Y', 'Redo'],
    ['Ctrl + E', 'Center align'],
    ['Ctrl + L', 'Left align'],
    ['Ctrl + R', 'Right align'],
    ['Ctrl + J', 'Justify'],
    ['Ctrl + K', 'Insert link'],
    ['Ctrl + Shift + 7', 'Numbered list'],
    ['Ctrl + Shift + 8', 'Bullet list'],
    ['Ctrl + ]', 'Indent'],
    ['Ctrl + [', 'Outdent'],
    ['Ctrl + \\', 'Clear formatting'],
    ['Ctrl + Shift + I', 'Toggle card inspector'],
    ['Tab', 'Indent list item'],
    ['Shift + Tab', 'Outdent list item'],
  ];

  const pdfShortcuts = [
    ['Page Up', 'Previous page'],
    ['Page Down', 'Next page'],
  ];

  let html = '<div class="shortcut-section"><h3>Global</h3>';
  html += globalShortcuts.map(([k, d]) => `<div class="shortcut-row"><kbd>${k}</kbd><span>${d}</span></div>`).join('');
  html += '</div>';

  if (activeModule === 'docs') {
    html += '<div class="shortcut-section"><h3>Documents</h3>';
    html += docsShortcuts.map(([k, d]) => `<div class="shortcut-row"><kbd>${k}</kbd><span>${d}</span></div>`).join('');
    html += '</div>';
  } else if (activeModule === 'pdf') {
    html += '<div class="shortcut-section"><h3>PDF</h3>';
    html += pdfShortcuts.map(([k, d]) => `<div class="shortcut-row"><kbd>${k}</kbd><span>${d}</span></div>`).join('');
    html += '</div>';
  } else if (activeModule === 'grid') {
    html += '<div class="shortcut-section"><h3>Spreadsheet</h3>';
    html += '<div class="shortcut-row"><span style="color:#999">Standard Excel shortcuts handled by Univer engine</span></div>';
    html += '</div>';
  }

  const modal = document.createElement('div');
  modal.id = 'shortcut-ref-modal';
  modal.innerHTML = `
    <div class="shortcut-ref-backdrop"></div>
    <div class="shortcut-ref-content">
      <div class="shortcut-ref-header">
        <h2>Keyboard Shortcuts</h2>
        <button class="shortcut-ref-close">&times;</button>
      </div>
      <div class="shortcut-ref-body">${html}</div>
    </div>
  `;

  document.body.appendChild(modal);

  // Close handlers
  modal.querySelector('.shortcut-ref-close').addEventListener('click', () => modal.remove());
  modal.querySelector('.shortcut-ref-backdrop').addEventListener('click', () => modal.remove());
}
